import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { Grid, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { DataManager } from '@syncfusion/ej2-data';
import {
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subYears,
} from 'date-fns';
import { take } from 'rxjs';
import { SessionUser } from 'src/app/model/session.user';
import { ApiService } from 'src/app/services/api.service';
import { CommonService } from 'src/app/services/common.service';
import { DashboardService } from 'src/app/services/dashboard.service';
import { LoginService } from 'src/app/services/login.service';
import { AuthDataManagerService } from 'src/app/shared/adaptors/authDataMangerService';
import { transformDateToApiFormat } from 'src/app/utils/date-util';
import {
    applyGridSearch,
    exportGridToExcel,
    getDashboardParams,
    getData,
    getFilters,
} from 'src/app/utils/grid-util';

@Component({
    selector: 'app-cashback-report',
    templateUrl: './cashback-report.component.html',
    styleUrl: './cashback-report.component.css',
})
export class CashbackReportComponent {
    @Output() submitSearch = new EventEmitter<void>();
    public data: object[] = [];
    public pageSettings: PageSettingsModel = { pageSize: 5 };
    filterSettings: any;
    filtersObj: any = {};
    cashbackReportList: DataManager;
    filters = {
        businessUnits: [],
        provinces: [],
        wholesalers: [],
        products: [],
        stores: [],
        suppliers: [],
        categories: [],
        timeRange: { from: '', to: '' },
    };
    showAdvancedFilters = false;
    supplierFieldKey = 'supplierIds';
    productFieldKey = 'productIds';
    businessFieldKey = 'supplierBusinessUnitIds';
    provinceFieldKey = 'provinceIds';
    wholesalerFieldKey = 'wholesalerIds';
    storeFieldKey = 'storeIds';
    categoryFieldKey = 'categoryIds';

    searchItemsObj: { [key: string]: string[] } = {};
    searchItems: { [key: string]: string[] } = {};
    user: SessionUser;
    sessionUser$ = this.loginService.sessionUser.asObservable();
    presetRanges: any = {};
    dateRange = { startDate: new Date(), endDate: new Date() };

    form = this.fb.group({
        productIds: new FormControl([]),
        wholesalerIds: new FormControl([]),
        storeIds: new FormControl([]),
        provinceIds: new FormControl([]),
        fromDate: new FormControl(''),
        toDate: new FormControl(''),
        fromDateFilter: new FormControl(''),
        toDateFilter: new FormControl(''),
        supplierBusinessUnitIds: new FormControl([]),
        supplierIds: new FormControl([]),
        categoryIds: new FormControl([]),
    });

    showAdvancedSearch = false;
    @ViewChild('cashbackReportGrid') gridInstance: Grid;
    isExporting: boolean = false;
    public createdAtFilterDate: Date | null = null;
    filterOperatorMap: { [key: string]: string } = {
        fromDate: 'greaterthan',
        toDate: 'lessthan',
        businessUnitIds: 'equal',
        productId: 'equal',
        provinceId: 'equal',
        storeId: 'equal',
        wholesalerId: 'equal',
        supplierBusinessUnitId: 'equal',
        supplierId: 'equal',
        categoryId: 'equal',
    };
    @ViewChild('createdAtDatePicker') createdAtDatePicker: any;
    isClearingFilters: boolean = false;
    isBusy: boolean = false;

    totalOffChainBalance: number = 0;
    totalOnChainBalance: number = 0;
    cashbackTotalToday: number = 0;
    cashbackAssetUrl: string = '';
    cashbackTokenTransferUrl: string = '';


    constructor(
        private authDataManagerService: AuthDataManagerService,
        private apiService: ApiService,
        private fb: FormBuilder,
        private loginService: LoginService,
        private commonService: CommonService,
        private dashboardService: DashboardService,
    ) { }

    ngOnInit(): void {
        this.pageSettings = { pageSizes: [20, 50, 100], pageSize: 20 };
        this.loginService.sessionUser.pipe(take(1)).subscribe((user) => {
            this.user = user;
            this.initializeDateRange();
            this.getFilters();
            this.init();
        });
    }

    async init() {
        await this.fetchCashbackReport();
        this.resetFilterSettings();
    }

    async fetchCashbackReport() {
        const endpoint = this.apiService.getApiEndpoint('sales');

        this.cashbackReportList = this.authDataManagerService.getDataManager({
            url: endpoint,
            statsCallback: (stats) => {
                this.totalOffChainBalance = stats?.totalOffChainBalance || 0;
                this.totalOnChainBalance = stats?.totalOnChainBalance || 0;
                this.cashbackTotalToday = stats?.cashbackTotalToday || 0;
                this.cashbackAssetUrl = stats?.cashbackAssetUrl || '';
                this.cashbackTokenTransferUrl = stats?.cashbackTokenTransferUrl || '';
            }
        });
    }

    setBusyState(val: boolean) {
        this.isBusy = val;
    }

    onChange(event: any, key: string) {
        let labelKey = 'name';
        if (key === this.businessFieldKey) labelKey = this.getBusinessFilterName();
        if (key === this.productFieldKey) labelKey = 'groupTitle';

        this.searchItemsObj[key] = event.map((e: any) => e[labelKey]);
    }

    getBusinessFilterName(): string {
        return this.user?.isAdmin() ? 'displayName' : 'name';
    }

    onClear(key: string) {
        this.doRemove([], '', key, '', '');
        if (!this.isClearingFilters) {
            this.fetchCashbackReport();
        }
    }

    doRemove(list: any[], value: string, key: string, labelKey: string, valKey: string) {
        this.searchItemsObj[key] = this.searchItemsObj[key]?.filter((v) => v !== value) || [];

        const updated = list.reduce((arr: any[], item: any) => {
            if (this.searchItemsObj[key]?.includes(item[labelKey])) {
                arr.push(item[valKey]);
            }
            return arr;
        }, []);

        this.form.get(key)?.setValue(updated);

        if (this.searchItemsObj[key]?.length < 1) {
            delete this.searchItemsObj[key];
            delete this.searchItems[key];
        }
    }

    toggleOption() {
        this.showAdvancedFilters = !this.showAdvancedFilters;
    }

    removeSearchItem(key: string, value: string) {
        // Use this.filters.* (populated by getFilters/getData), not any dead component-level
        // list field: an empty list here means doRemove rebuilds the form value from nothing,
        // silently clearing every selection for the field instead of just the removed chip.
        const filterMapping: { [key: string]: { list: any[]; label: string; valKey: string } } = {
            [this.supplierFieldKey]: { list: this.filters.suppliers, label: 'name', valKey: 'id' },
            [this.productFieldKey]: { list: this.filters.products, label: 'groupTitle', valKey: 'groupCode' },
            [this.storeFieldKey]: { list: this.filters.stores, label: 'name', valKey: 'id' },
            [this.businessFieldKey]: { list: this.filters.businessUnits, label: this.getBusinessFilterName(), valKey: 'id' },
            [this.provinceFieldKey]: { list: this.filters.provinces, label: 'name', valKey: 'id' },
            [this.wholesalerFieldKey]: { list: this.filters.wholesalers, label: 'name', valKey: 'id' },
            [this.categoryFieldKey]: { list: this.filters.categories, label: 'name', valKey: 'id' },
        };

        const mapping = filterMapping[key];
        if (mapping) {
            this.doRemove(mapping.list, value, key, mapping.label, mapping.valKey);
            if (!this.isClearingFilters) {
                this.fetchCashbackReport();
            }
        }
    }

    formatDates(msTimestamp: number): string {
        if (!msTimestamp) return '';

        const date = new Date(msTimestamp);
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' }); // e.g., "Jan"
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }

    async exportGridToExcel() {
        this.isExporting = true;
        await exportGridToExcel(this.gridInstance, this.formatDates, 'CashbackReport');
        this.isExporting = false;
    }

    onExcelCellInfo(args: any): void {
        if (args.column.field === 'createdAt' && args.value) {
            args.value = this.formatDates(args.value);
        }
        if (args.column.field === 'orderId' && args.value) {
            args.value = `${args.value.toString()}`;
        }
        if (args.column.field === 'categoryName' && (args.value === null || args.value === undefined)) {
            args.value = '-';
        }
        const currencyFields = ['netSales', 'vat', 'totalInclusive', 'cashback'];
        if (currencyFields.includes(args.column.field) && args.value !== null && args.value !== undefined) {
            args.value = Number(args.value);
            args.style = {
                numberFormat: '0.00'
            };
        }
    }

    onCreatedAtDateFilterChange(args: any): void {
        this.createdAtFilterDate = args.value;

        if (this.createdAtFilterDate) {
            const formattedDate = this.formatDate(this.createdAtFilterDate); // Format to match your data
            this.gridInstance.filterByColumn('createdAt', 'equal', formattedDate);
        } else {
            this.gridInstance.removeFilteredColsByField('createdAt');
        }
    }

    formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' }); // e.g., "Jan"
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    onDateFilterChange(args: any, field: string): void {
        const filterVal = args.value;
        if (filterVal) {
            this.gridInstance.filterByColumn(field, 'equal', filterVal);
        } else {
            this.gridInstance.removeFilteredColsByField(field);
        }
    }

    initializeDateRange(): void {
        const today = new Date();
        const yesterday = subDays(today, 1);
        const startWeek = startOfWeek(today, { weekStartsOn: 1 });
        const endWeek = endOfWeek(today, { weekStartsOn: 1 });

        this.presetRanges = {
            todayStart: today,
            todayEnd: today,
            yesterdayStart: yesterday,
            yesterdayEnd: yesterday,
            weekStart: startWeek,
            weekEnd: endWeek,
            lastWeekStart: subDays(startWeek, 7),
            lastWeekEnd: subDays(endWeek, 7),
            monthStart: startOfMonth(today),
            monthEnd: endOfMonth(today),
            lastMonthStart: startOfMonth(subMonths(today, 1)),
            lastMonthEnd: endOfMonth(subMonths(today, 1)),
            yearStart: startOfYear(today),
            yearEnd: endOfYear(today),
            lastYearStart: startOfYear(subYears(today, 1)),
            lastYearEnd: endOfYear(subYears(today, 1)),
        };

        // Set default date range to This Week
        this.form.patchValue({
            fromDateFilter: this.presetRanges.weekStart,
            toDateFilter: this.presetRanges.weekEnd,
            fromDate: transformDateToApiFormat(this.presetRanges.weekStart),
            toDate: transformDateToApiFormat(this.presetRanges.weekEnd),
        });
    }

    submit(changedFields: string) {
        if (!this.isClearingFilters) {
            this.getData(changedFields);
        }
    }

    resetFilterSettings() {
        const fromDate = this.form.get('fromDate')?.value;
        const toDate = this.form.get('toDate')?.value;
        const type = this.form.get('type')?.value;

        const filterColumns: any[] = [];

        filterColumns.push({
            field: 'cashback',
            operator: 'greaterthan',
            value: 0,
            predicate: 'and',
            ignoreCase: true,
            ignoreAccent: false,
        });

        filterColumns.push({
            field: 'type',
            operator: 'equal',
            value: type || 'buy',
            predicate: 'and',
        });

        if (this.user.isSupplierEnterprise()) {
            filterColumns.push({
                field: 'supplierId',
                operator: 'equal',
                value: this.user.enterprise.supplier.id,
                predicate: 'and',
            });
        }

        if (fromDate) {
            filterColumns.push({
                field: 'fromDate',
                operator: 'greaterthanorequal',
                value: fromDate,
                predicate: 'and',
            });
        }

        if (toDate) {
            filterColumns.push({
                field: 'toDate',
                operator: 'lessthanorequal',
                value: toDate,
                predicate: 'and',
            });
        }

        this.filterSettings = {
            showFilterBarStatus: false,
            mode: 'Immediate',
            columns: filterColumns,
            operators: {
                stringOperator: [
                    { value: 'contains', text: 'Contains' },
                    { value: 'startswith', text: 'Starts With' },
                    { value: 'endswith', text: 'Ends With' },
                    { value: 'equal', text: 'Equal' },
                ]
            }
        };
        this.gridInstance.filterSettings.columns = this.filterSettings;
    }

    processDates() {
        const fromDate = this.form.get('fromDateFilter')?.value;
        const toDate = this.form.get('toDateFilter')?.value;

        if (fromDate) {
            const formattedFromDate = transformDateToApiFormat(fromDate);
            this.form.get('fromDate')?.setValue(formattedFromDate);
        } else {
            this.form.get('fromDate')?.setValue(null);
        }

        if (toDate) {
            const formattedToDate = transformDateToApiFormat(toDate);
            this.form.get('toDate')?.setValue(formattedToDate);
        } else {
            this.form.get('toDate')?.setValue(null);
        }
    }

    clearFilters() {
        this.isClearingFilters = true;
        this.searchItems = {};
        this.searchItemsObj = {};
        this.createdAtFilterDate = null;
        if (this.createdAtDatePicker) {
            this.createdAtDatePicker.value = null;
        }
        const defaultFromDate = transformDateToApiFormat(this.presetRanges.weekStart);
        const defaultToDate = transformDateToApiFormat(this.presetRanges.weekEnd);

        this.form.patchValue({
            productIds: [],
            wholesalerIds: [],
            supplierBusinessUnitIds: [],
            provinceIds: [],
            storeIds: [],
            supplierIds: [],
            categoryIds: [],
            fromDateFilter: this.presetRanges.weekStart,
            toDateFilter: this.presetRanges.weekEnd,
            fromDate: defaultFromDate,
            toDate: defaultToDate,
        });

        setTimeout(() => {
            this.isClearingFilters = false;
            this.clearFilterTextInputsOnly();
            this.resetFilterSettings();
            this.getData('NoGridSearch');
        }, 1000);
    }

    clearFilterTextInputsOnly() {
        if (!this.gridInstance) return;
        const filterRowInputs = this.gridInstance.element.querySelectorAll('.e-filtertext') as NodeListOf<HTMLInputElement>;
        filterRowInputs.forEach(input => {
            input.value = '';
        });
    }

    getFilters() {
        getFilters(
            this.dashboardService,
            this.getDashboardParams.bind(this),
            this.user,
            (filters) => {
                this.filters = { ...this.filters, ...filters };
            },
            (loading) => {
                this.setBusyState(loading);
            },
        );
    }

    applyGridSearch(filter: { key: string; value: any; operators: string; }[]) {
        if (!this.isClearingFilters) {
            applyGridSearch(this.gridInstance, this.filterOperatorMap, filter);
        }
    }

    getDashboardParams(isInit: boolean, changedFields: string) {
        this.processDates();
        return getDashboardParams(
            this.form,
            this.user,
            this.filterOperatorMap,
            this.applyGridSearch.bind(this),
            isInit,
            changedFields
        );
    }

    getData(changedFields: string) {
        getData(
            this.dashboardService,
            this.getDashboardParams.bind(this),
            this.user,
            this.form,
            this.searchItemsObj,
            (items) => (this.searchItems = items),
            (filters) => (this.filters = { ...this.filters, ...filters }),
            (loading) => this.setBusyState(loading),
            changedFields
        );
    }
}
